import { ArrowDownUp } from "lucide-react";
import { Card, CardContent } from "./ui/card";
import CoachListSection from "./CoachListSection";
import LoadFailedCard from "./LoadFailedCard";
import { EmptyList, ListLoading, NOTES_FRAME, ROW_FRAME, SelectBox } from "./CoachNotesKit";
import { MATCH_REFLECTION_SORTS, sortRows } from "../lib/coachListSort";

/**
 * Issue #376(b) — the PLAYER's own match reflections, read-only: what they
 * wrote about their own innings, straight after logging it or later via
 * Reflections > Match Reflections. No composer — this is the player's
 * writing, not the coach's; a coach who wants to say something back uses
 * Feedback.
 *
 * Lifted out of `CoachPortal.jsx` for issue #460 (see `CoachGoalsPanel.jsx`
 * for why), gaining only the sort control.
 */
export default function CoachMatchReflectionsPanel(props) {
  // #1221 slice 4: the coach's player page passes `appearance="r5"` and gets the
  // kit version below. Unset (the flag-off page) is the shipped panel, words and all.
  if (props.appearance === "r5") return <MatchReflectionsPanelR5 {...props} />;
  return <ShippedCoachMatchReflectionsPanel {...props} />;
}

function ShippedCoachMatchReflectionsPanel({ reflections, sort, onSortChange, firstName }) {
  const rows = sortRows(MATCH_REFLECTION_SORTS, sort, reflections);

  return (
    <Card className="border-slate-200">
      <CardContent className="p-6">
        <CoachListSection
          title="Match reflections"
          sorts={MATCH_REFLECTION_SORTS}
          sortValue={sort}
          onSortChange={onSortChange}
          sortTestId="cp-match-reflections-sort"
          listTestId="portal-match-reflections"
          description={
            <p className="text-sm text-slate-500 mt-1" data-testid="cp-match-reflections-copy">
              What {firstName} wrote about their own innings, in their own words.
            </p>
          }
        >
          {rows.length === 0 && (
            <p className="text-sm text-slate-500" data-testid="cp-match-reflections-empty">
              No match reflections from {firstName} yet.
            </p>
          )}
          {rows.map((r) => (
            <div key={r.reflection_id} className="p-3 rounded-lg border border-slate-200" data-testid={`cp-match-reflection-${r.reflection_id}`}>
              <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">
                {[r.match_opponent ? `vs ${r.match_opponent}` : null, r.match_date].filter(Boolean).join(" · ") || "Match"}
              </div>
              <div className="text-sm text-slate-800 whitespace-pre-wrap">{r.body}</div>
              <div className="text-xs text-slate-500 mt-1.5">
                {r.created_at?.slice(0, 10)}{r.updated_at ? " · edited" : ""}
              </div>
            </div>
          ))}
        </CoachListSection>
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// #1221 slice 4 (D240, D251) — `CoachPlayerReflections.dc.html`: the Reflections
// section, the player's own match reflections, read only. Same read as before
// (`GET /reflections?kind=match`); this slice adds no read and no write and no
// control that changes a word. The words are the player's: a coach who wants to
// say something back uses Feedback. "Reflections" means one thing on this page
// now, what the player wrote; the coach's own notes are Private notes.
//
// Loading, empty and failed are three different nodes (D53 ruling 2). The
// eyebrow and heading lead all of them, so a coach knows what the section is
// before the list arrives.
// ---------------------------------------------------------------------------

function rowEyebrow(r) {
  return [r.match_opponent ? `vs ${r.match_opponent}` : null, r.match_date].filter(Boolean).join(" · ") || "Match";
}

function MatchReflectionsPanelR5({ status = "loaded", reflections, sort, onSortChange, firstName, onRetry }) {
  const rows = sortRows(MATCH_REFLECTION_SORTS, sort, reflections);
  const filled = status === "loaded" && rows.length > 0;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14, minWidth: 0 }}>
      <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <h2 style={{ margin: 0, fontFamily: "'Manrope', sans-serif", fontWeight: 800, fontSize: 20, letterSpacing: "-0.3px", color: "var(--nw-ink)" }}>Reflections</h2>
        <p data-testid="cp-match-reflections-eyebrow" style={{ margin: 0, fontSize: 13, lineHeight: 1.5, color: "var(--nw-ink-muted)" }}>
          {`Written by ${firstName}, about their own matches.`}
        </p>
      </div>

      <div style={NOTES_FRAME}>
        <h3 style={{ margin: 0, fontFamily: "'Manrope', sans-serif", fontWeight: 700, fontSize: 17, color: "var(--nw-ink)", overflowWrap: "anywhere" }}>
          {`${firstName}'s match reflections`}
        </h3>

        {status === "loading" && <ListLoading testId="cp-match-reflections-loading" />}

        {status === "failed" && (
          <LoadFailedCard title="Couldn't load this player's match reflections" onRetry={onRetry} testId="cp-match-reflections-load-failed" />
        )}

        {status === "loaded" && !filled && (
          <EmptyList testId="cp-match-reflections-empty" title={`No match reflections from ${firstName} yet.`} />
        )}

        {filled && (
          <>
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              <SelectBox
                icon={ArrowDownUp}
                value={sort}
                onChange={onSortChange}
                ariaLabel="Sort match reflections"
                testId="cp-match-reflections-sort"
                options={MATCH_REFLECTION_SORTS.map((s) => ({ value: s.value, label: s.label }))}
              />
            </div>
            <p data-testid="cp-match-reflections-copy" style={{ margin: 0, fontSize: 13, lineHeight: 1.5, color: "var(--nw-ink-muted)" }}>
              {`What ${firstName} wrote after each match, in their own words. You can read them; you can't change them.`}
            </p>
            <div data-testid="portal-match-reflections" style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {rows.map((r) => (
                <div key={r.reflection_id} data-testid={`cp-match-reflection-${r.reflection_id}`} style={ROW_FRAME}>
                  <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.16em", textTransform: "uppercase", color: "var(--nw-ink-muted)", overflowWrap: "anywhere" }}>
                    {rowEyebrow(r)}
                  </div>
                  <div style={{ fontSize: 14, lineHeight: 1.5, color: "var(--nw-ink)", whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>{r.body}</div>
                  <div style={{ fontSize: 12, lineHeight: 1.5, color: "var(--nw-ink-muted)" }}>
                    {r.created_at?.slice(0, 10)}{r.updated_at ? " · edited" : ""}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
